// src/services/hbaAuthService.js
//
// Authenticator-based HBA token acquisition flow.
//
// Flow:
//   1. GET <fhirHost>/owner-authenticate
//        → { challenge_path, state, redirect_uri? }
//      (fhirHost is the Matrix homeserver URL which proxies VZD calls;
//       in the reference env this maps to fhir-directory-ref.vzd.ti-dienste.de)
//   2. window.open("authenticator://?challenge_path=...&callback=tim")
//        → Authenticator app reads HBA smartcard via IDP service
//        → Redirects browser tab to /hba?code=<authCode>
//   3. src/index.jsx detects /hba pre-React and posts the auth code via
//      BroadcastChannel("vzd-auth-code")
//   4. Main tab receives authCode from BroadcastChannel
//   5. GET <redirectUri>?code=<authCode>&state=<state>  →  HBA access_token
//
// Security notes:
//   - The auth code and access_token are never logged.
//   - The BroadcastChannel is scoped to the same origin.
//   - A 5-minute timeout prevents indefinite hangs.

import logger from '../utils/logger';
import matrixService from './matrixService';
import { getMatrixOpenIdAccessToken } from './openIdToken';
import { timFetch } from '../utils/timUserAgent';

const BROADCAST_CHANNEL_NAME = 'vzd-auth-code';
const AUTH_CODE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

// Path to the challenge endpoint, relative to the homeserver base URL.
// The homeserver proxies this to the VZD FHIR directory per gematik spec.
const CHALLENGE_PATH = '/owner-authenticate';

class HbaAuthService {
  constructor() {
    this._pending = null; // { channel, cleanup, reject } for the active waitForAuthCode
  }

  /**
   * Step 1 — Fetch challenge from the homeserver.
   * @param {string} baseUrl  homeserver base URL
   * @param {AbortSignal} [signal]
   * @returns {Promise<{ challenge_path: string, state: string, redirect_uri?: string }>}
   */
  async getAuthChallenge(baseUrl, signal) {
    const url = `${baseUrl}${CHALLENGE_PATH}`;
    logger.debug('[HbaAuthService] Fetching auth challenge from', url);
    const res = await timFetch(url, { method: 'GET', signal });
    if (!res.ok) throw new Error(`Challenge endpoint (${url}) returned HTTP ${res.status}.`);
    const data = await res.json();
    // Log the response keys (not values) to help identify the correct field name
    logger.debug('[HbaAuthService] Challenge response keys:', Object.keys(data || {}));
    // The gematik VZD spec calls this field "challenge_path" but some environments
    // may use a different key. Check the browser console for the actual field names.
    const rawChallenge = data?.challenge_path ?? data?.challengePath ?? data?.challenge ?? data?.authorizationUrl ?? data?.authorization_url;
    if (!rawChallenge) {
      throw new Error(
        `No challenge_path in /owner-authenticate response. Got keys: [${Object.keys(data || {}).join(', ')}]. Check browser console for details.`,
      );
    }

    // If the value looks like a JWT (no protocol scheme) rather than a full URL,
    // construct the IDP authorization URL by decoding the JWT's `iss` claim.
    // The Authenticator app validates that the URL host matches the `iss` — so
    // we must use exactly the issuer from the token, not a hardcoded value.
    const isUrl = rawChallenge.startsWith('http://') || rawChallenge.startsWith('https://');
    let challengePath;
    let jwtPayload = null;
    if (isUrl) {
      // Re-parse and re-serialize the URL so the browser's URL API normalises
      // any unencoded characters in query params (e.g. space in "scope=fhir-vzd openid").
      try {
        const parsed = new URL(rawChallenge);
        challengePath = parsed.toString();
      } catch {
        challengePath = rawChallenge;
      }
    } else {
      // VZD returned a raw JWT challenge. Decode its claims and reconstruct
      // the full IDP auth URL that the Authenticator expects, e.g.:
      // https://idp.../auth?response_type=code&client_id=...&scope=...&redirect_uri=...&state=...&code_challenge=...&code_challenge_method=S256
      try {
        jwtPayload = JSON.parse(atob(rawChallenge.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        logger.debug('[HbaAuthService] JWT iss claim:', jwtPayload?.iss);
      } catch {
        throw new Error('Cannot decode challenge JWT payload.');
      }
      if (!jwtPayload?.iss) {
        throw new Error('Cannot determine IDP URL: the challenge JWT has no `iss` claim.');
      }
      const idpUrl = new URL('/auth', jwtPayload.iss);
      // Reconstruct standard OAuth2 PKCE params from JWT claims
      idpUrl.searchParams.set('response_type', jwtPayload.response_type || 'code');
      if (jwtPayload.client_id) idpUrl.searchParams.set('client_id', jwtPayload.client_id);
      if (jwtPayload.scope) idpUrl.searchParams.set('scope', jwtPayload.scope);
      if (jwtPayload.redirect_uri) idpUrl.searchParams.set('redirect_uri', jwtPayload.redirect_uri);
      if (jwtPayload.state) idpUrl.searchParams.set('state', jwtPayload.state);
      if (jwtPayload.code_challenge) idpUrl.searchParams.set('code_challenge', jwtPayload.code_challenge);
      if (jwtPayload.code_challenge_method) idpUrl.searchParams.set('code_challenge_method', jwtPayload.code_challenge_method);
      challengePath = idpUrl.toString();
      logger.debug('[HbaAuthService] Reconstructed IDP auth URL from JWT claims:', jwtPayload.iss + '/auth');
    }

    // Ensure state and redirect_uri are always returned — they may only exist inside
    // the JWT claims and not as top-level fields in the /owner-authenticate response.
    return {
      ...data,
      challenge_path: challengePath,
      state: data.state ?? jwtPayload?.state,
      redirect_uri: data.redirect_uri ?? jwtPayload?.redirect_uri,
    };
  }

  /**
   * Extract the redirect_uri embedded inside the challenge URL.
   * The VZD encodes its own signin endpoint as `redirect_uri` in the challenge
   * query params — that is the endpoint the messenger must POST the auth code to.
   * Falls back to the caller-supplied origin-based URI if not present.
   * @param {string} challengePath  fully-formed IDP authorization URL
   * @param {string} fallbackUri    e.g. `${window.location.origin}/hba`
   * @returns {string}
   */
  extractRedirectUri(challengePath, fallbackUri) {
    try {
      const params = new URL(challengePath).searchParams;
      const embedded = params.get('redirect_uri');
      if (embedded) {
        logger.debug('[HbaAuthService] Using redirect_uri from challenge URL');
        return embedded;
      }
    } catch {
      // malformed URL — fall through
    }
    logger.debug('[HbaAuthService] No redirect_uri in challenge URL, using fallback');
    return fallbackUri;
  }

  /**
   * Step 2 — Open the authenticator deep link.
   * @param {string} challengePath
   * @returns {string} the redirect URI the authenticator will call back to
   * @throws {Error} if the popup was blocked by the browser
   */
  launchAuthenticator(challengePath) {
    const redirectUri = `${window.location.origin}/hba`;
    // encodeURIComponent handles any unencoded chars (including spaces) correctly.
    // Do NOT pre-replace spaces — that causes double-encoding (%20 → %2520).
    const deepLink = `authenticator://?challenge_path=${encodeURIComponent(challengePath)}&callback=tim`;
    logger.debug('[HbaAuthService] challenge_path value:', challengePath);
    logger.debug('[HbaAuthService] Deep link:', deepLink);
    logger.info('[HbaAuthService] Launching authenticator deep link');
    const win = window.open(deepLink, '_blank');
    // A null return generally indicates the popup was blocked. Some browsers
    // (notably iOS Safari) may also return null for legitimately-handled
    // custom-scheme URLs, and others (Firefox) may return a non-null Window
    // even when no protocol handler is registered — so this is a popup-blocker
    // signal, not a definitive "handler missing" probe. Fail fast either way
    // so the user gets actionable feedback instead of a 5-min timeout.
    if (!win) {
      const err = new Error('Could not open the authenticator. Please allow pop-ups for this site and try again.');
      err.code = 'POPUP_BLOCKED';
      throw err;
    }
    return redirectUri;
  }

  /* - Messenger sends Get challenge path request to VZD - https://fhir-directory-ref.vzd.ti-dienste.de/owner-authenticate
- VZD (Relying agent) creates the PKCE CODE_VERIFIER and calculates the CODE_CHALLENGE based on the CODE_CHALLENGE_METHOD algorithm
- messenger gets the challenge path: https://idp-ref.app.ti-dienste.de/auth?response_type=code&client_id=GEMgematFHI4HkPrd8SR&scope=fhir-vzd+openid&redirect_uri=https%3A%2F%2Ffhir-directory-ref.vzd.ti-dienste.de%2Fsignin-gematik-idp-dienst&state=YQlEXgpj3kSYt2_Xw63_Pg&code_challenge=clLlmAclmwMFHmytupbzjWu0ZqUaaJBg-_R9IzX66Lg&code_challenge_method=S256
- Opens authenticator deeplink and passes the challenge path.
-  authenticator calls 3 endpoints on the IDP service. First one is the discovery endpoint shown below:
	- https://idp-ref.app.ti-dienste.de/.well-known/openid-configuration, gets the discovery document - a Base64-encoded metadata document that contains most of the information required for an application to perform a login. This includes information such as the interfaces to use and the location of the IDP service 's public signing keys 

  */

  /**
   * Step 3 — Wait for the auth code posted by /hba via BroadcastChannel.
   * Resolves with `{ code, state }` (state may be undefined if the callback
   * page did not include it) or rejects on timeout / abort / cancel.
   * @param {AbortSignal} [signal]
   * @returns {Promise<{ code: string, state?: string }>}
   */
  waitForAuthCode(signal) {
    return new Promise((resolve, reject) => {
      // Cancel any previously pending listener (rejects its promise too).
      this._cancelPending(new Error('Superseded by a new authenticator flow.'));

      // If the caller's signal is already aborted, reject immediately.
      if (signal?.aborted) {
        const err = new Error('Aborted');
        err.name = 'AbortError';
        reject(err);
        return;
      }

      const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);

      const cleanup = () => {
        clearTimeout(timer);
        channel.close();
        if (signal && abortHandler) signal.removeEventListener('abort', abortHandler);
        if (this._pending && this._pending.channel === channel) this._pending = null;
      };

      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('Authenticator timed out. Please try again.'));
      }, AUTH_CODE_TIMEOUT_MS);

      channel.onmessage = (event) => {
        const code = event.data?.code;
        const state = event.data?.state;
        if (!code) {
          cleanup();
          reject(new Error('Received empty auth code from authenticator.'));
          return;
        }
        cleanup();
        resolve({ code, state });
      };

      channel.onmessageerror = () => {
        cleanup();
        reject(new Error('BroadcastChannel message error.'));
      };

      const abortHandler = signal ? () => {
        cleanup();
        const err = new Error('Aborted');
        err.name = 'AbortError';
        reject(err);
      } : null;
      if (abortHandler) signal.addEventListener('abort', abortHandler, { once: true });

      // Track everything the canceller needs to abort an outstanding wait.
      this._pending = { channel, cleanup, reject };
    });
  }

  /**
   * Step 4 — Exchange the auth code for an HBA access token.
   * @param {string} redirectUri
   * @param {string} code
   * @param {string} [state]
   * @param {AbortSignal} [signal]
   * @returns {Promise<{ access_token: string, expires_in: number }>}
   */
  async exchangeAuthCode(redirectUri, code, state, signal) {
    const url = new URL(redirectUri);
    url.searchParams.set('code', code);
    if (state) url.searchParams.set('state', state);

    // The VZD signin endpoint requires a Matrix OpenID token to verify the
    // caller is a legitimate TI-M client. A regular Matrix access token is
    // not federatable — the VZD validates the token against the homeserver's
    // federation endpoint, so we must use the short-lived OpenID token.
    const headers = {};
    try {
      const client = matrixService.getClient();
      if (client) {
        const openIdToken = await getMatrixOpenIdAccessToken(client, { signal });
        if (openIdToken) headers['X-Authorization'] = `Bearer ${openIdToken}`;
      }
    } catch {
      // proceed without header — server will return 401 if required
    }

    const res = await timFetch(url.toString(), { method: 'GET', signal, headers });
    if (!res.ok) throw new Error(`HBA token exchange HTTP ${res.status}`);
    const data = await res.json();
    if (!data?.access_token) throw new Error('No access_token in HBA token exchange response');
    return data;
  }

  /**
   * Cancel any pending BroadcastChannel listener (e.g. on unmount or retry).
   * Also clears the 5-min timer and rejects the outstanding promise so the
   * awaiting caller does not hang.
   */
  cancelPendingAuth() {
    const err = new Error('Authenticator wait cancelled.');
    err.name = 'AbortError';
    this._cancelPending(err);
  }

  _cancelPending(err) {
    const pending = this._pending;
    if (!pending) return;
    this._pending = null;
    pending.cleanup();
    pending.reject(err);
  }
}

export default new HbaAuthService();
